import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@src/environments/environment';
import { Observable } from 'rxjs';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { EquipoSimple, SubequiposResponse, EquiposTorneoResponse, EquipoDetalleResponse, EquipoCompleto, EquipoGlobal, GetEquiposGlobalResponse, WaSaldo, FusionarEquiposPayload, PreviewFusionEquiposResponse, FusionarEquiposResponse, EquipoConfig, EquipoConfigResponse, WaCreditTransaccionesResponse, CrearWaCreditTransaccionPayload, WaCreditTransaccionResponse, CrearEquipoPayload, CrearEquipoResponse, BuscarEquiposResponse, VincularEquipoLigaResponse, LigasPortalResponse, LimpiarEquipoResponse, EnlaceActualizarJugadorResponse } from '@app/models/equipos/equipos.model';
import { LigasModalidadesResponse } from '@app/models/jugadores/jugador.model';


@Injectable({
  providedIn: 'root',
})
export class EquiposService {
  private _http = inject(HttpClient);
  private _url = environment.url;
  private _servicioSesion = inject(ServicioSesion);

  private _getAuthHeaders(): HttpHeaders {
    const token = this._servicioSesion.obtenerToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Obtiene la lista general de equipos
   */
  public getEquipos(): Observable<{ data: EquipoSimple[] } | EquipoSimple[]> {
    return this._http.get<any>(`${this._url}/equipos`, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * Consulta pública (sin auth) de nombre y escudo de un equipo por su nombre_corto.
   * Usado por el portal del jugador para mostrar la identidad del equipo antes de identificarse.
   * Lectura sin captcha (se dispara sola al cargar la página) — el backend la protege con throttle.
   */
  public getEquipoPorNombreCorto(nombreCorto: string): Observable<{
    estado: boolean;
    equipo: {
      nombre: string;
      escudoURL: string | null;
      colores: { color_primary: string | null; color_primary_light: string | null; color_secondary: string | null; color_titulo: string | null; color_bg: string | null } | null;
    };
  }> {
    return this._http.get<any>(`${this._url}/equipos/slug/${nombreCorto}`);
  }

  /** Ligas a las que pertenece el equipo — para selectores (ej. horarios de disponibilidad). */
  public getLigasDeEquipo(equipoId: number): Observable<{ estado: boolean; ligas: { id: string; nombre: string }[] }> {
    return this._http.get<{ estado: boolean; ligas: { id: string; nombre: string }[] }>(
      `${this._url}/equipos/${equipoId}/ligas`,
      { headers: this._getAuthHeaders() }
    );
  }

  /** Ligas del equipo (origen + adicionales) con logo y slug, para accesos al portal. */
  public getLigasPortalDeEquipo(equipoId: number): Observable<LigasPortalResponse> {
    return this._http.get<LigasPortalResponse>(
      `${this._url}/equipos/${equipoId}/ligas-portal`,
      { headers: this._getAuthHeaders() }
    );
  }
  /** Ligas del equipo (origen + adicionales) con sus modalidades, para elegir a cuál se inscribe un jugador. */
  public getLigasModalidadesDeEquipo(equipoId: number): Observable<LigasModalidadesResponse> {
    return this._http.get<LigasModalidadesResponse>(
      `${this._url}/equipos/${equipoId}/ligas-modalidades`,
      { headers: this._getAuthHeaders() }
    );
  }

  /**
 * Obtiene la lista completa de equipos con sus subequipos anidados
 * para la administración global.
 */
public getEquiposGlobal(torneoId?: number | null, ligaId?: string | null): Observable<GetEquiposGlobalResponse> {
  let params = new HttpParams();
  if (torneoId) {
    params = params.set('torneo_id', torneoId);
  }
  if (ligaId) {
    params = params.set('liga_id', ligaId);
  }
  return this._http.get<GetEquiposGlobalResponse>(`${this._url}/equipos`, {
    headers: this._getAuthHeaders(),
    params
  });
}

  /**
   * Obtiene los subequipos participantes en torneo filtrando por Equipo Padre
   */
  public getSubequipos(torneoId: number, equipoId: number): Observable<SubequiposResponse> {
    const url = `${this._url}/torneos/${torneoId}/equipos/${equipoId}/subequipos`;
    return this._http.get<SubequiposResponse>(url, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * Obtiene la lista de equipos que juegan en un torneo específico,
   * incluyendo ya sus subequipos anidados.
   */
  public getEquiposPorTorneo(torneoId: number): Observable<EquiposTorneoResponse> {
    const url = `${this._url}/torneos/${torneoId}/equipos`;
    return this._http.get<EquiposTorneoResponse>(url, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * Obtiene la info de un equipo incluyendo sus subequipos y estadísticas generales
   */
  public getEquipoPorId(id: number): Observable<EquipoDetalleResponse> {
      return this._http.get<EquipoDetalleResponse>(`${this._url}/equipos/${id}`, {
          headers: this._getAuthHeaders()
      });
  }

  /**
   *
   * @param id
   * @param data
   * @returns
   */
  public actualizarColores(
    equipoId: number,
    data: { colorBackDisplay?: string | null; colorTextDisplay?: string | null; colorJersey1?: string | null; colorJersey2?: string | null; colorJersey3?: string | null; colorJersey4?: string | null; ligaId?: string }
  ): Observable<any> {
    const { ligaId, ...resto } = data;
    return this._http.patch(`${this._url}/equipos/${equipoId}/colores`, { ...resto, liga_id: ligaId }, {
      headers: this._getAuthHeaders(),
    });
  }

  public actualizarEquipo(id: number, data: EquipoCompleto): Observable<any> {
    return this._http.put(`${this._url}/equipos/${id}`, data, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * PATCH /equipos/{id}/nombre-corto
   * Unico canal para cambiar el nombre corto — tambien genera/actualiza el enlace
   * de BetterLinks y urlActualizarJugador en el backend.
   */
  public actualizarNombreCorto(id: number, nombreCorto: string): Observable<{ estado: boolean; mensaje: string; nombre_corto: string; urlActualizarJugador: string | null }> {
    return this._http.patch<{ estado: boolean; mensaje: string; nombre_corto: string; urlActualizarJugador: string | null }>(
      `${this._url}/equipos/${id}/nombre-corto`, { nombre_corto: nombreCorto }, { headers: this._getAuthHeaders() }
    );
  }

  /**
   * GET /equipos/{id}/config
   * Obtiene la configuración (colores de interfaz + saldos WA) del equipo.
   * El backend crea el registro si el equipo todavía no tiene uno.
   */
  public getEnlaceActualizarJugador(id: number): Observable<EnlaceActualizarJugadorResponse> {
    return this._http.get<EnlaceActualizarJugadorResponse>(`${this._url}/equipos/${id}/enlace-actualizar-jugador`, {
      headers: this._getAuthHeaders()
    });
  }

  // Elige el nombre corto (una sola vez) y genera el link corto; sin nombre corto nuevo solo crea el link corto faltante
  public crearEnlaceActualizarJugador(id: number, nombreCorto: string | null): Observable<EnlaceActualizarJugadorResponse> {
    return this._http.post<EnlaceActualizarJugadorResponse>(`${this._url}/equipos/${id}/enlace-actualizar-jugador`,
      { nombre_corto: nombreCorto }, { headers: this._getAuthHeaders() });
  }

  public getEquipoConfig(id: number): Observable<EquipoConfigResponse> {
    return this._http.get<EquipoConfigResponse>(`${this._url}/equipos/${id}/config`, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * PUT /equipos/{id}/config
   * Actualiza los colores de interfaz del equipo (color_primary, color_primary_light,
   * color_secondary, color_titulo, color_bg).
   */
  public actualizarEquipoConfig(
    id: number,
    data: Partial<Pick<EquipoConfig, 'color_primary' | 'color_primary_light' | 'color_secondary' | 'color_titulo' | 'color_bg' | 'wa_monthly_limit' | 'accesosActivos' | 'dominioEnlaces'>>
  ): Observable<EquipoConfigResponse> {
    return this._http.put<EquipoConfigResponse>(`${this._url}/equipos/${id}/config`, data, {
      headers: this._getAuthHeaders()
    });
  }

  public getWaSaldo(equipoId: number): Observable<WaSaldo> {
    return this._http.get<WaSaldo>(`${this._url}/whatsapp/saldo`, {
      headers: this._getAuthHeaders(),
      params: { equipo_id: equipoId }
    });
  }

  /**
   * GET /whatsapp/transacciones?equipo_id=
   * Histórico de compras/asignaciones de créditos WhatsApp para un equipo.
   */
  public getWaTransacciones(equipoId: number, page: number = 1, perPage: number = 10): Observable<WaCreditTransaccionesResponse> {
    return this._http.get<WaCreditTransaccionesResponse>(`${this._url}/whatsapp/transacciones`, {
      headers: this._getAuthHeaders(),
      params: { equipo_id: equipoId, per_page: perPage, page }
    });
  }

  /**
   * POST /whatsapp/transacciones
   * Registra una transacción de créditos y suma a wa_extra_balance del equipo.
   * El backend fija "registradoPor" con el usuario autenticado.
   */
  public crearWaTransaccion(data: CrearWaCreditTransaccionPayload): Observable<WaCreditTransaccionResponse> {
    return this._http.post<WaCreditTransaccionResponse>(`${this._url}/whatsapp/transacciones`, data, {
      headers: this._getAuthHeaders()
    });
  }

  /**
   * Crea un nuevo equipo en la liga, junto con sus colores de calendario,
   * su equipo_config y, opcionalmente, su crm_config, en la misma petición.
   */
  public crearEquipo(data: CrearEquipoPayload): Observable<CrearEquipoResponse> {
    return this._http.post<CrearEquipoResponse>(
      `${this._url}/equipos`, data, { headers: this._getAuthHeaders() }
    );
  }

  /**
   * Búsqueda difusa (por palabra suelta, no frase exacta) de equipos que aún
   * no pertenecen a `excluirLigaId`, para detectar duplicados antes de crear
   * un equipo nuevo.
   */
  public buscarEquipos(nombre: string, excluirLigaId?: string | null): Observable<BuscarEquiposResponse> {
    let params = new HttpParams().set('nombre', nombre);
    if (excluirLigaId) {
      params = params.set('excluir_liga_id', excluirLigaId);
    }
    return this._http.get<BuscarEquiposResponse>(
      `${this._url}/equipos/buscar`, { headers: this._getAuthHeaders(), params }
    );
  }

  /**
   * Vincula un equipo ya existente (de otra liga) a la liga del contexto
   * activo, en vez de crear uno nuevo duplicado.
   */
  public vincularEquipoALiga(equipoId: number, ligaId?: string | null): Observable<VincularEquipoLigaResponse> {
    const body = ligaId ? { liga_id: ligaId } : {};
    return this._http.post<VincularEquipoLigaResponse>(
      `${this._url}/equipos/${equipoId}/vincular-liga`, body, { headers: this._getAuthHeaders() }
    );
  }

  // Saca al equipo de una liga (ROOT, o admin de esa misma liga); conserva su histórico de torneos.
  public desvincularEquipoDeLiga(equipoId: number, ligaId: string): Observable<{ estado: boolean; mensaje: string }> {
    return this._http.delete<{ estado: boolean; mensaje: string }>(
      `${this._url}/equipos/${equipoId}/ligas/${ligaId}`, { headers: this._getAuthHeaders() }
    );
  }

  /**
   * Genera un resumen de lo que ocurrirá al fusionar equipos, sin modificar nada.
   */
  public previewFusionEquipos(payload: FusionarEquiposPayload): Observable<PreviewFusionEquiposResponse> {
    return this._http.post<PreviewFusionEquiposResponse>(
      `${this._url}/equipos/fusionar/preview`, payload, { headers: this._getAuthHeaders() }
    );
  }

  /**
   * Fusiona uno o más equipos origen en un equipo destino y borra los orígenes.
   */
  public fusionarEquipos(payload: FusionarEquiposPayload): Observable<FusionarEquiposResponse> {
    return this._http.post<FusionarEquiposResponse>(
      `${this._url}/equipos/fusionar`, payload, { headers: this._getAuthHeaders() }
    );
  }

  // Borrado físico de un equipo demo (solo ROOT); confirmar=false solo regresa el reporte
  public limpiarEquipo(equipoId: number, confirmar: boolean): Observable<LimpiarEquipoResponse> {
    return this._http.post<LimpiarEquipoResponse>(
      `${this._url}/admin/limpiar-equipo/${equipoId}`, { confirmar }, { headers: this._getAuthHeaders() }
    );
  }
}
